/**
 * Template field format shared by the editor, TemplateEditor, the quality score and the
 * Cloud Run worker (`cloud-run-worker/templateFields.js` mirrors these semantics exactly).
 *
 * The implementation is a dependency-free HTML tokenizer (no DOMParser) so it behaves the
 * same in the browser, in Jest and in Node.
 *
 * Format (CONTRACTS §5):
 *   Inline field: <span data-tpl-field="f_xxxxxxxx" data-kind="text" data-required="true"
 *                       data-source="any" data-max-words="120">Instruction for the AI</span>
 *   Block field:  <div data-tpl-field="f_xxxxxxxx" data-kind="rich|list|table_rows|text" ...>Instruction</div>
 *   Variables:    {{project.name}} {{customer.name}} {{doc.date}} {{doc.version}} {{doc.title}}
 *   Repeat rows:  <tr data-tpl-repeat="requirements" data-filter="type=User Story">…</tr>
 *   Locked:       any element with data-tpl-locked="true"
 *
 * Exact semantics (the worker must match these):
 *  - Text inside <code>, <pre>, <script>, <style>, HTML comments and inside existing
 *    data-tpl-field elements is never scanned for legacy placeholders or variables.
 *  - A legacy placeholder is `{body}` inside ONE text run (it never spans tags) where the `{`
 *    is not preceded by `{`, the `}` is not followed by `}`, and body has no `{` or `}`.
 *    `{?body}` marks an optional field. Empty bodies (after removing `?` and trimming) are ignored.
 *  - Instruction normalization (for ids and the `instruction` field): decode HTML entities,
 *    replace NBSP with space, collapse whitespace runs to one space, trim.
 *  - Legacy ids: 'f_' + 8 lowercase hex chars of 32-bit FNV-1a over UTF-16 code units of
 *    `${ordinal}:${instruction}`, where ordinal is the 0-based index of the placeholder among
 *    all legacy placeholders of the document, in document order.
 *  - Block migration: a <p> whose only content is a single text run that, trimmed, is exactly
 *    one placeholder, and which has no <li> ancestor, is replaced by a block field <div>
 *    (data-kind="rich"). Its data-block-id attribute (if any) is carried over. Every other
 *    placeholder becomes an inline <span> field (data-kind="text").
 *  - Field attribute defaults when parsing: kind = 'text' for <span>, 'rich' otherwise
 *    (unknown values fall back to that default); required = true unless data-required="false";
 *    source ∈ requirements|sessions|project|any (default any); maxWords = positive integer or null.
 *  - Storage form (backend compatibility): the editor serializes a field's instruction wrapped
 *    in legacy braces, `<div data-tpl-field …>{Instruction}</div>` (`{?Instruction}` when
 *    optional), so the pre-field generation pipeline (which only fills `{…}` text) keeps
 *    working. When a field's normalized text is exactly one such `{…}` / `{?…}` group, the
 *    braces (and the `?`) are not part of the instruction.
 */

/** Allowed field output kinds. */
export const TEMPLATE_FIELD_KINDS = ['text', 'rich', 'list', 'table_rows'];

/** Allowed field data sources. */
export const TEMPLATE_FIELD_SOURCES = ['requirements', 'sessions', 'project', 'any'];

/** Variables resolved in code (never by the LLM), offered in the editor's variable picker. */
export const TEMPLATE_VARIABLES = [
  { name: 'project.name', label: 'Project name' },
  { name: 'customer.name', label: 'Customer name' },
  { name: 'doc.title', label: 'Document title' },
  { name: 'doc.date', label: 'Date generated' },
  { name: 'doc.version', label: 'Document version' },
];

/** Variables available inside `data-tpl-repeat="requirements"` rows. */
export const TEMPLATE_REPEAT_VARIABLES = [
  { name: 'req.id', label: 'Requirement id' },
  { name: 'req.title', label: 'Requirement title' },
  { name: 'req.description', label: 'Requirement description' },
  { name: 'req.priority', label: 'Requirement priority' },
];

const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr',
]);
const OPAQUE_ELEMENTS = new Set(['code', 'pre', 'script', 'style']);
const TOKEN_RE = /<!--[\s\S]*?-->|<\/([a-zA-Z][a-zA-Z0-9-]*)\s*>|<([a-zA-Z][a-zA-Z0-9-]*)((?:\s+[^\s"'>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)\s*(\/?)>/g;
const ATTR_RE = /([^\s"'>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
const VARIABLE_RE = /\{\{\s*([a-zA-Z_][\w.]*)\s*\}\}/g;
const PRESERVED_ALIAS_RE = /^(?:IMAGE|MERMAID)_/i;

const NAMED_ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'",
};

/**
 * 32-bit FNV-1a over UTF-16 code units.
 * @param {string} input - String to hash.
 * @returns {number} Unsigned 32-bit hash.
 */
export function fnv1a32(input) {
  let hash = 0x811c9dc5;
  const str = String(input);
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * Deterministic field id for a migrated legacy placeholder.
 * @param {number} ordinal - 0-based index among legacy placeholders in document order.
 * @param {string} instruction - Normalized instruction text.
 * @returns {string} Field id such as `f_1a2b3c4d`.
 */
export function legacyFieldId(ordinal, instruction) {
  return `f_${fnv1a32(`${ordinal}:${instruction}`).toString(16).padStart(8, '0')}`;
}

/**
 * Random field id for fields created in the editor.
 * @returns {string} Field id such as `f_9f86d081`.
 */
export function createFieldId() {
  let hex = '';
  try {
    const bytes = new Uint8Array(4);
    const cryptoApi = typeof window !== 'undefined' && window.crypto ? window.crypto : null;
    if (!cryptoApi) throw new Error('no crypto');
    cryptoApi.getRandomValues(bytes);
    hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    hex = Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0');
  }
  return `f_${hex}`;
}

/**
 * Decode the HTML entities that can appear in text runs and attribute values.
 * @param {string} value - Raw HTML text.
 * @returns {string} Decoded text.
 */
export function decodeEntities(value) {
  return String(value || '').replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+|#39);/g, (match, body) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return match;
      try {
        return String.fromCodePoint(code);
      } catch {
        return match;
      }
    }
    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named !== undefined ? named : match;
  });
}

/**
 * Escape text for safe inclusion in HTML text content or a double-quoted attribute.
 * @param {string} value - Plain text.
 * @returns {string} Escaped HTML.
 */
export function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Normalize instruction text: decode entities, NBSP to space, collapse whitespace, trim.
 * @param {string} rawText - Raw (possibly entity-encoded) text.
 * @returns {string} Normalized instruction.
 */
export function normalizeInstruction(rawText) {
  return decodeEntities(rawText).replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Remove the legacy brace wrapper from a field's normalized text (see "Storage form").
 * `{Write the summary}` and `{?Write the summary}` become `Write the summary`; any other text
 * (including text with inner braces) is returned unchanged.
 * @param {string} text - Normalized field text.
 * @returns {string} Instruction without the wrapper.
 */
export function unwrapFieldInstruction(text) {
  const value = String(text == null ? '' : text);
  const match = /^\{\??([^{}]*)\}$/.exec(value);
  return match ? match[1].trim() : value;
}

/**
 * Text content a field element is saved with: the instruction in legacy braces, so older
 * generation pipelines that only replace `{…}` placeholders still fill the field.
 * @param {string} instruction - Instruction text (without braces).
 * @param {boolean} [required=true] - Whether the field is required (`{?…}` when false).
 * @returns {string} Storage text, or '' for an empty instruction.
 */
export function wrapFieldInstruction(instruction, required = true) {
  // Inner braces would make the wrapper ambiguous (and unparseable on reload): use parentheses.
  const text = unwrapFieldInstruction(String(instruction == null ? '' : instruction).trim())
    .replace(/[{}]/g, (c) => (c === '{' ? '(' : ')'));
  if (!text) return '';
  return required === false ? `{?${text}}` : `{${text}}`;
}

/**
 * Parse a raw attribute string into a lowercase-keyed map.
 * @param {string} raw - Attribute source, e.g. ` class="a" data-x='1'`.
 * @returns {Record<string, string>} Attribute map (values decoded).
 */
function parseAttributes(raw) {
  const attrs = {};
  if (!raw) return attrs;
  ATTR_RE.lastIndex = 0;
  let match = ATTR_RE.exec(raw);
  while (match !== null) {
    const name = match[1].toLowerCase();
    const value = match[2] ?? match[3] ?? match[4] ?? '';
    attrs[name] = decodeEntities(value);
    match = ATTR_RE.exec(raw);
  }
  return attrs;
}

/**
 * Split HTML into text, open, close and comment tokens.
 * @param {string} html - Source HTML.
 * @returns {Array<{type: 'text'|'open'|'close'|'comment', raw: string, name?: string, attrs?: Record<string,string>, selfClosing?: boolean}>} Tokens.
 */
function tokenize(html) {
  const source = String(html || '');
  const tokens = [];
  let lastIndex = 0;
  TOKEN_RE.lastIndex = 0;
  let match = TOKEN_RE.exec(source);
  while (match !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', raw: source.slice(lastIndex, match.index) });
    }
    const raw = match[0];
    if (raw.startsWith('<!--')) {
      tokens.push({ type: 'comment', raw });
    } else if (match[1]) {
      tokens.push({ type: 'close', raw, name: match[1].toLowerCase() });
    } else {
      const name = match[2].toLowerCase();
      tokens.push({
        type: 'open',
        raw,
        name,
        attrs: parseAttributes(match[3]),
        selfClosing: Boolean(match[4]) || VOID_ELEMENTS.has(name),
      });
    }
    lastIndex = match.index + raw.length;
    match = TOKEN_RE.exec(source);
  }
  if (lastIndex < source.length) {
    tokens.push({ type: 'text', raw: source.slice(lastIndex) });
  }
  return tokens;
}

/**
 * Walk tokens while tracking the open-element stack, opaque regions (code/pre/script/style)
 * and template field regions. Calls `visit` for every token with its context.
 * @param {Array<object>} tokens - Tokens from {@link tokenize}.
 * @param {(token: object, index: number, ctx: {stack: string[], opaqueDepth: number, fieldDepth: number}) => void} visit - Visitor.
 * @returns {void}
 */
function walk(tokens, visit) {
  const stack = [];
  let opaqueDepth = 0;
  let fieldDepth = 0;
  tokens.forEach((token, index) => {
    if (token.type === 'open') {
      visit(token, index, { stack, opaqueDepth, fieldDepth });
      if (!token.selfClosing) {
        const isField = Object.prototype.hasOwnProperty.call(token.attrs, 'data-tpl-field');
        stack.push({ name: token.name, opaque: OPAQUE_ELEMENTS.has(token.name), field: isField });
        if (OPAQUE_ELEMENTS.has(token.name)) opaqueDepth += 1;
        if (isField) fieldDepth += 1;
      }
      return;
    }
    if (token.type === 'close') {
      let at = stack.length - 1;
      while (at >= 0 && stack[at].name !== token.name) at -= 1;
      if (at >= 0) {
        for (let i = stack.length - 1; i >= at; i -= 1) {
          if (stack[i].opaque) opaqueDepth -= 1;
          if (stack[i].field) fieldDepth -= 1;
        }
        stack.length = at;
      }
      visit(token, index, { stack, opaqueDepth, fieldDepth });
      return;
    }
    visit(token, index, { stack, opaqueDepth, fieldDepth });
  });
}

/**
 * Find legacy `{…}` / `{?…}` placeholders in one raw text run.
 * @param {string} rawText - Raw text (entities still encoded).
 * @returns {Array<{start: number, end: number, rawBody: string, optional: boolean, instruction: string}>} Matches.
 */
function findLegacyPlaceholders(rawText) {
  const results = [];
  const re = /\{([^{}]*)\}/g;
  let match = re.exec(rawText);
  while (match !== null) {
    const start = match.index;
    const end = start + match[0].length;
    const before = start > 0 ? rawText[start - 1] : '';
    const after = end < rawText.length ? rawText[end] : '';
    if (before !== '{' && after !== '}') {
      let body = match[1];
      let optional = false;
      const trimmedLeading = body.replace(/^\s+/, '');
      if (trimmedLeading.startsWith('?')) {
        optional = true;
        body = trimmedLeading.slice(1);
      }
      const instruction = normalizeInstruction(body);
      if (instruction) {
        results.push({ start, end, rawBody: body.trim(), optional, instruction });
      }
    }
    match = re.exec(rawText);
  }
  return results;
}

/**
 * Read a field definition from an element's attributes.
 * @param {string} tagName - Lowercase tag name.
 * @param {Record<string, string>} attrs - Element attributes.
 * @param {string} instruction - Normalized instruction text.
 * @param {number} ordinal - Index among fields in document order.
 * @returns {{id: string, kind: string, required: boolean, source: string, maxWords: number|null, instruction: string, ordinal: number, isBlock: boolean}} Field.
 */
function readField(tagName, attrs, instruction, ordinal) {
  const isBlock = tagName !== 'span';
  const defaultKind = isBlock ? 'rich' : 'text';
  const kind = TEMPLATE_FIELD_KINDS.includes(String(attrs['data-kind'] || '').trim())
    ? String(attrs['data-kind']).trim()
    : defaultKind;
  const source = TEMPLATE_FIELD_SOURCES.includes(String(attrs['data-source'] || '').trim())
    ? String(attrs['data-source']).trim()
    : 'any';
  const maxWordsRaw = parseInt(attrs['data-max-words'], 10);
  const id = String(attrs['data-tpl-field'] || '').trim() || legacyFieldId(ordinal, instruction);
  return {
    id,
    kind,
    required: String(attrs['data-required'] || '').trim().toLowerCase() !== 'false',
    source,
    maxWords: Number.isFinite(maxWordsRaw) && maxWordsRaw > 0 ? maxWordsRaw : null,
    instruction,
    ordinal,
    isBlock,
  };
}

/**
 * Parse every template field, variable and repeat row in a template.
 * Legacy placeholders are only counted (`legacyCount`); call {@link migrateLegacyPlaceholders}
 * first to turn them into fields.
 * @param {string} html - Template HTML.
 * @returns {{fields: Array<{id: string, kind: string, required: boolean, source: string, maxWords: number|null, instruction: string, ordinal: number, isBlock: boolean}>, variables: string[], repeats: Array<{source: string, filter: string|null}>, legacyCount: number}} Parsed template structure.
 */
export function parseTemplateFields(html) {
  const tokens = tokenize(html);
  const fields = [];
  const variables = [];
  const variableSet = new Set();
  const repeats = [];
  let legacyCount = 0;
  let openField = null;

  walk(tokens, (token, index, ctx) => {
    if (token.type === 'open') {
      if (Object.prototype.hasOwnProperty.call(token.attrs, 'data-tpl-repeat')) {
        repeats.push({
          source: String(token.attrs['data-tpl-repeat'] || '').trim() || 'requirements',
          filter: token.attrs['data-filter'] ? String(token.attrs['data-filter']).trim() : null,
        });
      }
      if (!openField && Object.prototype.hasOwnProperty.call(token.attrs, 'data-tpl-field')) {
        if (token.selfClosing) {
          fields.push(readField(token.name, token.attrs, '', fields.length));
        } else {
          openField = { token, depth: ctx.stack.length, text: '' };
        }
      }
      return;
    }
    if (token.type === 'close') {
      if (openField && ctx.stack.length === openField.depth) {
        fields.push(readField(openField.token.name, openField.token.attrs, unwrapFieldInstruction(normalizeInstruction(openField.text)), fields.length));
        openField = null;
      }
      return;
    }
    if (token.type !== 'text') return;
    if (openField) {
      openField.text += token.raw;
    }
    if (ctx.opaqueDepth > 0) return;
    const decoded = decodeEntities(token.raw);
    VARIABLE_RE.lastIndex = 0;
    let match = VARIABLE_RE.exec(decoded);
    while (match !== null) {
      const name = match[1];
      if (!PRESERVED_ALIAS_RE.test(name) && !variableSet.has(name)) {
        variableSet.add(name);
        variables.push(name);
      }
      match = VARIABLE_RE.exec(decoded);
    }
    if (ctx.fieldDepth === 0 && !openField) {
      legacyCount += findLegacyPlaceholders(token.raw).length;
    }
  });

  if (openField) {
    fields.push(readField(openField.token.name, openField.token.attrs, unwrapFieldInstruction(normalizeInstruction(openField.text)), fields.length));
  }

  return { fields, variables, repeats, legacyCount };
}

/**
 * Build the HTML for a template field element.
 * @param {{id: string, isBlock: boolean, kind?: string, required?: boolean, source?: string, maxWords?: number|null, blockId?: string|null}} field - Field definition.
 * @param {string} innerHtml - Escaped instruction HTML.
 * @returns {string} Field element HTML.
 */
export function buildFieldHtml(field, innerHtml) {
  const tag = field.isBlock ? 'div' : 'span';
  const kind = field.kind || (field.isBlock ? 'rich' : 'text');
  const parts = [
    `data-tpl-field="${escapeHtml(field.id)}"`,
    `data-kind="${escapeHtml(kind)}"`,
    `data-required="${field.required === false ? 'false' : 'true'}"`,
    `data-source="${escapeHtml(field.source || 'any')}"`,
  ];
  if (field.maxWords) parts.push(`data-max-words="${Number(field.maxWords)}"`);
  if (field.blockId) parts.push(`data-block-id="${escapeHtml(field.blockId)}"`);
  return `<${tag} ${parts.join(' ')}>${innerHtml}</${tag}>`;
}

/**
 * Convert legacy `{instruction}` / `{?instruction}` placeholders into template fields.
 * Idempotent: running it on its own output returns the same string.
 * @param {string} html - Template HTML.
 * @returns {string} Migrated HTML (unchanged string when there is nothing to migrate).
 */
export function migrateLegacyPlaceholders(html) {
  const source = typeof html === 'string' ? html : '';
  if (!source || source.indexOf('{') === -1) return source;
  const tokens = tokenize(source);
  const replacements = new Map();
  let ordinal = 0;

  walk(tokens, (token, index, ctx) => {
    if (token.type !== 'text') return;
    if (ctx.opaqueDepth > 0 || ctx.fieldDepth > 0) return;
    const matches = findLegacyPlaceholders(token.raw);
    if (matches.length === 0) return;

    const prev = tokens[index - 1];
    const next = tokens[index + 1];
    const parent = ctx.stack[ctx.stack.length - 1];
    const insideListItem = ctx.stack.some((entry) => entry.name === 'li');
    const isSoleParagraphContent = matches.length === 1
      && parent && parent.name === 'p'
      && prev && prev.type === 'open' && prev.name === 'p'
      && next && next.type === 'close' && next.name === 'p'
      && token.raw.trim() === token.raw.slice(matches[0].start, matches[0].end)
      && !insideListItem;

    if (isSoleParagraphContent) {
      const m = matches[0];
      const field = {
        id: legacyFieldId(ordinal, m.instruction),
        isBlock: true,
        kind: 'rich',
        required: !m.optional,
        source: 'any',
        blockId: prev.attrs['data-block-id'] || null,
      };
      ordinal += 1;
      replacements.set(index - 1, '');
      replacements.set(index + 1, '');
      replacements.set(index, buildFieldHtml(field, m.rawBody));
      return;
    }

    let out = '';
    let cursor = 0;
    matches.forEach((m) => {
      out += token.raw.slice(cursor, m.start);
      out += buildFieldHtml({
        id: legacyFieldId(ordinal, m.instruction),
        isBlock: false,
        kind: 'text',
        required: !m.optional,
        source: 'any',
      }, m.rawBody);
      ordinal += 1;
      cursor = m.end;
    });
    out += token.raw.slice(cursor);
    replacements.set(index, out);
  });

  if (replacements.size === 0) return source;
  return tokens.map((token, index) => (replacements.has(index) ? replacements.get(index) : token.raw)).join('');
}

/**
 * Resolve a dotted variable path against a context object.
 * Flat keys (`{'project.name': 'X'}`) take precedence over nested paths.
 * @param {object} ctx - Variable context.
 * @param {string} name - Dotted name such as `project.name`.
 * @returns {string|null} Resolved string value or null when unknown.
 */
function lookupVariable(ctx, name) {
  if (!ctx || typeof ctx !== 'object') return null;
  if (Object.prototype.hasOwnProperty.call(ctx, name)) {
    const flat = ctx[name];
    return flat == null || typeof flat === 'object' ? null : String(flat);
  }
  let current = ctx;
  const segments = name.split('.');
  for (let i = 0; i < segments.length; i += 1) {
    if (current == null || typeof current !== 'object' || !Object.prototype.hasOwnProperty.call(current, segments[i])) {
      return null;
    }
    current = current[segments[i]];
  }
  return current == null || typeof current === 'object' ? null : String(current);
}

/**
 * Replace `{{variable}}` tokens outside code/pre with escaped values from `ctx`.
 * Unknown variables are left untouched so later stages (or the user) can see them.
 * @param {string} html - Template or document HTML.
 * @param {object} ctx - Context such as `{ project: { name }, customer: { name }, doc: { date, version, title } }`.
 * @returns {string} HTML with known variables resolved.
 */
export function resolveVariables(html, ctx) {
  const source = typeof html === 'string' ? html : '';
  if (!source || source.indexOf('{{') === -1) return source;
  const tokens = tokenize(source);
  const replacements = new Map();
  walk(tokens, (token, index, ctx2) => {
    if (token.type !== 'text' || ctx2.opaqueDepth > 0 || token.raw.indexOf('{{') === -1) return;
    const replaced = token.raw.replace(VARIABLE_RE, (match, name) => {
      const value = lookupVariable(ctx, name);
      return value == null ? match : escapeHtml(value);
    });
    if (replaced !== token.raw) replacements.set(index, replaced);
  });
  if (replacements.size === 0) return source;
  return tokens.map((token, index) => (replacements.has(index) ? replacements.get(index) : token.raw)).join('');
}

/**
 * Count stray single braces left outside fields, variables and code (used by the quality score).
 * @param {string} html - Template HTML (ideally already migrated).
 * @returns {number} Number of `{` / `}` characters that are not part of `{{var}}` or a field.
 */
export function countStrayBraces(html) {
  const tokens = tokenize(html);
  let count = 0;
  walk(tokens, (token, index, ctx) => {
    if (token.type !== 'text' || ctx.opaqueDepth > 0 || ctx.fieldDepth > 0) return;
    const withoutVariables = decodeEntities(token.raw).replace(/\{\{[^{}]*\}\}/g, ' ');
    count += (withoutVariables.match(/[{}]/g) || []).length;
  });
  return count;
}
